import './styles/general.css'
import { HashRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
// import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'

import ProtectedRoute from './components/ProtectedRoute'
import MainScreen from './screens/MainScreen'
import AboutLMWScreen from './screens/AboutLMWScreen'
import FAQScreen from './screens/FAQScreen'
import MarketingPlaceholderScreen from './screens/MarketingPlaceholderScreen'
import DataCenterScreen from './screens/DataCenterScreen'
import SearchScreen from './screens/SearchScreen'
import HomeScreen from './screens/HomeScreen'
import DashboardScreen from './screens/DashboardScreen'
import CreateEditDashboardScreen from './screens/CreateEditDashboardScreen'
import DashboardDetailsScreen from './screens/DashboardDetailsScreen'
import FavoriteScreen from './screens/FavoriteScreen'
import AccountScreen from './screens/AccountScreen'
import GoogleValidator from './screens/GoogleValidator'
import ResetPasswordScreen from './screens/ResetPasswordForgotScreen'
import AccountActivated from './components/AccountActivated'
import CookieConsentBanner from './components/CookieConsentBanner'
import Test from './screens/Test'
import PublicDashboardScreen from './screens/PublicDashboardScreen'
import ShareLinkLandingScreen from './screens/ShareLinkLandingScreen'
import { trackPageVisit } from './utils/tracking'
import { scheduleScrollAppToTop } from './utils/scrollToTop'
// import ViewerScreen from './screens/ViewerScreen'

const PENDING_SHARE_TOKEN_KEY = 'pendingShareToken'

// A user who lands on a share-invite link while signed out may go through a
// sign-up + email-verification detour (register -> check email -> activate
// -> log in from wherever that link opens, not necessarily this screen).
// ShareLinkLandingScreen stashes the token in sessionStorage before that
// detour; this watches for the next successful login anywhere in the app
// and, if a pending token is waiting, consumes it and routes straight back
// to the invitation instead of leaving the user on /home.
function PendingShareInviteRedirect() {
  const navigate = useNavigate()
  const userInfo = useSelector(state => state.userLogin?.userInfo)
  const wasAuthenticated = useRef(Boolean(userInfo))

  useEffect(() => {
    const justAuthenticated = !wasAuthenticated.current && Boolean(userInfo)
    wasAuthenticated.current = Boolean(userInfo)

    if (!justAuthenticated) return

    const pendingToken = sessionStorage.getItem(PENDING_SHARE_TOKEN_KEY)
    if (!pendingToken) return

    sessionStorage.removeItem(PENDING_SHARE_TOKEN_KEY)
    navigate(`/share/dashboard/${pendingToken}`)
  }, [userInfo, navigate])

  return null
}

function RouteVisitTracker() {
  const location = useLocation()

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  useLayoutEffect(() => {
    return scheduleScrollAppToTop()
  }, [location.pathname, location.search])

  useEffect(() => {
    trackPageVisit({
      pageName: document.title || location.pathname,
      routePath: location.pathname,
    })
  }, [location.pathname])

  return null
}

function App() {
  return (
    <main>
      <Router>
        <RouteVisitTracker />
        <PendingShareInviteRedirect />
        <Routes>
          <Route path='/' Component={MainScreen} exact />
          <Route path='/about' Component={AboutLMWScreen} exact />
          <Route path='/faq' Component={FAQScreen} exact />
          <Route path='/data-center' Component={DataCenterScreen} exact />
          <Route path='/country-briefings' element={<Navigate to='/' replace />} />
          <Route path='/insights' element={<Navigate to='/' replace />} />
          <Route path='/methodology' Component={MarketingPlaceholderScreen} exact />
          <Route path='/search' element={<ProtectedRoute isMandatory={false}><SearchScreen /></ProtectedRoute>} />
          <Route path='/home' element={<ProtectedRoute isMandatory={true}><HomeScreen /></ProtectedRoute>} />
          <Route path='/dashboard' element={<ProtectedRoute isMandatory={true}><DashboardScreen /></ProtectedRoute>} exact />
          <Route path='/dashboard/edit' element={<ProtectedRoute isMandatory={true}><CreateEditDashboardScreen /></ProtectedRoute>} />
          <Route path='/dashboard/:code' element={<ProtectedRoute isMandatory={true}><DashboardDetailsScreen /></ProtectedRoute>} />
          <Route path='/public/dashboard/:token' element={<PublicDashboardScreen />} />
          {/* Deliberately NOT wrapped in ProtectedRoute: that would bounce an
              unauthenticated visitor to '/' and lose the token before the
              screen ever gets a chance to stash it for post-login resume. */}
          <Route path='/share/dashboard/:token' element={<ShareLinkLandingScreen />} />
          <Route path='/favorite' element={<ProtectedRoute isMandatory={true}><FavoriteScreen /></ProtectedRoute>} />
          <Route path='/account' element={<ProtectedRoute isMandatory={true}><AccountScreen /></ProtectedRoute>} />
          <Route path='/help' element={<ProtectedRoute isMandatory={true}><FAQScreen /></ProtectedRoute>} />
          <Route path='/test' element={<Test />} />

          <Route path='/reset-password/:url_token' element={<ProtectedRoute isMandatory={false}><ResetPasswordScreen /></ProtectedRoute>} />

          <Route path='/activate-account' element={<ProtectedRoute isMandatory={false}><AccountActivated /></ProtectedRoute>} />

          <Route path='/google-validator' element={<GoogleValidator />} />

          {/* <Route path='/viewer' Component={ViewerScreen} /> */}
        </Routes>
        <CookieConsentBanner />
      </Router>
    </main>
  );
}

export default App;
